import { Fragment, useState, useEffect } from 'react';
import { C, FONT, SectionHeader, TimeframeControlWithDates, useTimeframe, rangeLabel, DEFAULT_MARKER_NAME, returningLine } from './adminShared';
import { getAdminUtmStats, getAdminWistiaStats } from '../../utils/api';

const PLACEHOLDER = { total: 0, leads: [] };

// ── The Traffic tab's links ─────────────────────────────────────────────────
// THERE IS NO COPY OF ANY LINK LIST IN THIS FILE ANY MORE, AND THERE MUST NOT
// BE ONE AGAIN.
//
// Two lists used to live here, both duplicating backend/src/config/adNames.js:
//   • ORGANIC_LINK_SPECS — the named opt-in/sales links, under a "keep the two
//     lists in sync" comment. Four consecutive sessions had to make the
//     identical edit twice.
//   • CAMPAIGN_UTM_LINKS — the paid Ads Manager URLs, repeating slugs that are
//     already keys in ACTIVE_CAMPAIGNS.
//
// Both failed the same silent way: the backend bucketed a click using ITS copy
// while this file rendered a link built from ITS OWN, so real traffic dropped
// into 'Other (untracked)' / 'Other (unmapped)' with the link still on screen
// and no error anywhere.
//
// All three lists — campaigns, optin, sales — now arrive in the /utm-stats
// response as `utmLinks`, built from that one file. Adding or renaming a link
// is one edit in adNames.js. If you are tempted to paste a list back in here so
// the boxes render without the API, don't — that recreates the bug.
const EMPTY_UTM_LINKS = { campaigns: [], campaignsDm: [], optin: [], sales: [], masterclassDirect: [], clarityCall: [] };

// ── Traffic hierarchy (rendered from the backend's nested structure) ────────
// Levels: 1 = paid/organic/direct · 2 = campaign · 3 = placement platform.
// Level 3 is collapsed by default; 1 and 2 are always visible.

// Opt-in rate is shown ONLY where it means something.
//
//  • landed === 0        — a percentage of zero landings is noise.
//  • optedIn > landed    — the two numbers cover different time windows. Opt-ins
//                          go back to launch; landings only start the day the
//                          opt-in page began recording them. A campaign that ran
//                          before then produces ratios like 158%, which is not a
//                          conversion rate, it is a number that reads as broken.
//
// Both cases render '—' and are covered by the note under the table.
const rateOf = (landed, optedIn) =>
  landed > 0 && optedIn <= landed ? `${Math.round((optedIn / landed) * 100)}%` : null;

const fmtTrackingDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Numeric columns are a single fixed width so they line up down the whole
// table and a child visibly sums into its parent. 46px fits 'Opted in' at the
// header size and any count Ben will realistically see, without stealing label
// room on a phone.
const COL_W = 46;
const RATE_W = 38;

// Light teal wash for the PAID CAMPAIGN rows only (a wayfinding cue so the ads
// stand apart from organic sources at a glance). Cooler/bluer than the palette's
// mint greenBg (#F0FAF5) so it reads as distinct, low-saturation so it sits
// quietly against the cream bg and keeps the brown/terracotta figures readable.
const CAMPAIGN_TINT = '#E3F1EF';

// ── Sales page table ────────────────────────────────────────────────────────
// Three columns instead of the opt-in section's two, and NO drill-down:
// ry_video_sessions stores no utm_content-level ad data, so campaign depth is
// as far as this can honestly go.
const SALES_COLS = ['Landed', 'CTA', 'Bought'];

const SalesColumnHeader = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, padding: '0 8px 6px', borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
    <span style={{ flex: 1, minWidth: 0 }} />
    {SALES_COLS.map(h => (
      <span key={h} style={{ width: COL_W, textAlign: 'right', fontSize: 9.5, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {h}
      </span>
    ))}
  </div>
);

const SalesRow = ({ label, landed, cta, purchased, isTotal, muted }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: isTotal ? '9px 8px' : '7px 8px',
    background: isTotal ? C.barTrack : 'transparent',
    borderBottom: isTotal ? 'none' : `1px solid ${C.border}`,
    borderRadius: 8,
  }}>
    <span style={{
      flex: 1, minWidth: 0, fontSize: isTotal ? 13 : 12.5,
      fontWeight: isTotal ? 800 : 500,
      color: muted ? C.accent : C.heading,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>{label}</span>
    {[landed, cta, purchased].map((n, i) => (
      <span key={i} style={{ width: COL_W, textAlign: 'right', fontSize: isTotal ? 13 : 12, fontWeight: isTotal ? 800 : 600, color: n > 0 ? C.heading : C.accent }}>
        {n}
      </span>
    ))}
  </div>
);

const SalesTable = ({ sales }) => {
  if (!sales) return <div style={{ fontSize: 12, color: C.accent, padding: '8px 0' }}>No sales-page data yet.</div>;
  const { links = [], totals = { landed: 0, cta: 0, purchased: 0 } } = sales;
  return (
    <div>
      <SalesColumnHeader />
      {links.map(r => (
        <SalesRow key={r.label} label={r.label} landed={r.landed} cta={r.cta} purchased={r.purchased}
          muted={r.label === 'Not tagged / unmatched'} />
      ))}
      {/* Section total — totals THIS section only. There is deliberately no
          combined total with the opt-in section above. */}
      <div style={{ marginTop: 8 }}>
        <SalesRow label="Total" landed={totals.landed} cta={totals.cta} purchased={totals.purchased} isTotal />
      </div>
      <div style={{ paddingTop: 10, fontSize: 10, color: C.accent, lineHeight: 1.6 }}>
        <strong style={{ color: C.text, fontWeight: 700 }}>Since 14 August 2026</strong> — the same
        basis as the Opt-in page section. Counted as people, not visits, and filtered the same way.
        Anyone whose link carried no tags — including everyone arriving from an untagged
        source — sits in <strong>Not tagged / unmatched</strong> rather than being dropped.
      </div>
    </div>
  );
};

// ── MASTERCLASS SECTION ─────────────────────────────────────────────────────
// Sits between the opt-in and sales rows so the tab reads as the funnel in
// order. Two questions, because either alone is half the story: where the
// arrival came from, and what they did once they were there.
//
// FED BY /wistia-stats, NOT /utm-stats. That is the same endpoint the Pages &
// Masterclass tab uses, so "landed" here and "page loads" there are the same
// number by construction rather than two computations that agree today and
// drift tomorrow.

// One label + one count. Same rhythm as SalesRow, one column instead of three.
const McRow = ({ label, count, muted, isTotal, indent }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: isTotal ? '9px 8px' : '7px 8px',
    paddingLeft: indent ? 20 : 8,
    background: isTotal ? C.barTrack : 'transparent',
    borderBottom: isTotal ? 'none' : `1px solid ${C.border}`,
    borderRadius: 8,
  }}>
    <span style={{
      flex: 1, minWidth: 0, fontSize: isTotal ? 13 : 12.5,
      fontWeight: isTotal ? 800 : 500, color: muted ? C.accent : C.heading,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>{label}</span>
    <span style={{
      width: COL_W, textAlign: 'right', fontSize: isTotal ? 13 : 12,
      fontWeight: isTotal ? 800 : 600, color: count > 0 ? C.heading : C.accent,
    }}>{count}</span>
  </div>
);

const McHeading = ({ children }) => (
  <div style={{
    fontSize: 9.5, fontWeight: 700, color: C.accent, textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '0 8px 6px', borderBottom: `1px solid ${C.border}`, marginBottom: 6,
  }}>{children}</div>
);

const MasterclassTable = ({ mc }) => {
  if (!mc) {
    return (
      <div style={{ fontSize: 12, color: C.accent, padding: '8px 0', lineHeight: 1.6 }}>
        Couldn’t load the masterclass figures — the stats API didn’t respond. The two
        sections either side are unaffected; reload to try again.
      </div>
    );
  }

  // ── EVERY FIGURE IN THIS SECTION IS A PERSON COUNT ──────────────────────
  // `landed` was mc.pageLoads (raw page loads). It is now uniqueVisitorsAll —
  // read from the endpoint rather than computed here, and the SAME field the
  // Pages & Masterclass card shows as "Unique visitors", so the two match by
  // construction and not by coincidence.
  //
  // Page loads are still available and still shown, clearly labelled as a
  // separate line, so Ben can compare the two the way that card does.
  const landed  = mc.uniqueVisitorsAll ?? 0;
  const loads   = mc.pageLoads ?? 0;
  const src     = mc.sources;
  const played  = mc.totalPlays ?? 0;
  const half    = mc.milestones?.p50?.count ?? 0;
  const most    = mc.milestones?.p90?.count ?? 0;
  const clicked = mc.cta?.clicked?.count ?? 0;

  return (
    <div>
      <McHeading>Where they came from</McHeading>
      {src
        ? (
          <>
            {/* A tagged link that has never been used still renders, at zero —
                the backend seeds those three rows from MASTERCLASS_DIRECT_SPECS,
                the same array that generates the copyable links below, so a link
                Ben can copy always has a row here. Muted at zero so it reads as
                "watching, nothing yet" rather than as an error or clutter; it
                takes normal weight the moment it carries a real number. Only the
                tagged rows can ever be zero — every other bucket exists because
                something landed in it. */}
            {src.buckets.map(b => (
              <McRow key={b.label} label={b.label} count={b.count} muted={b.count === 0} />
            ))}
            {src.unattributable > 0 && (
              <McRow label="Not attributable" count={src.unattributable} muted />
            )}
            <div style={{ marginTop: 8 }}>
              <McRow label="Total people who landed" count={src.total} isTotal />
            </div>
            <div style={{ paddingTop: 10, fontSize: 10, color: C.accent, lineHeight: 1.6 }}>
              One row per <strong style={{ color: C.text, fontWeight: 700 }}>person</strong>, counted
              once under where they <em>first</em> arrived from. Someone who comes from the opt-in on
              Monday and an email link on Wednesday is one person on the opt-in row, not two people —
              which is why these rows add up to the total underneath them exactly.
              {' '}The three tagged links above only went live on{' '}
              <strong style={{ color: C.text, fontWeight: 700 }}>25 August 2026</strong>, so nobody
              who arrived before then can carry one. Almost everyone reaching this page still gets
              here by opting in and being redirected straight through, which is why that row holds
              nearly all of it. A tagged row at zero means <em>not used yet</em>, not <em>nobody came
              that way</em>.
              {src.unattributable > 0 && (
                <> <strong style={{ color: C.text, fontWeight: 700 }}>Not attributable</strong> is the{' '}
                {src.unattributable} {src.unattributable === 1 ? 'person' : 'people'} the page never
                recorded a landing event for — for them it stored neither a referrer nor a source, so
                no rule could ever place them. They are counted in the total rather than hidden.</>
              )}
            </div>
          </>
        )
        : (
          <div style={{ fontSize: 12, color: C.accent, padding: '8px 0', lineHeight: 1.6 }}>
            Source breakdown not available — the backend is probably still deploying. Reload in a
            minute. The watch figures below are unaffected.
          </div>
        )}

      <div style={{ marginTop: 20 }}>
        <McHeading>What they did on the page</McHeading>
        <McRow label="People who landed"       count={landed} />
        <McRow label="Started watching"        count={played}  indent />
        <McRow label="Watched half"            count={half}    indent />
        <McRow label="Watched most (90%)"      count={most}    indent />
        <McRow label="Clicked through to the sales page" count={clicked} indent />
        <div style={{ marginTop: 8 }}>
          <McRow label="Page loads (same people, counted per visit)" count={loads} muted />
        </div>
        <div style={{ paddingTop: 10, fontSize: 10, color: C.accent, lineHeight: 1.6 }}>
          Every figure above is <strong style={{ color: C.text, fontWeight: 700 }}>people</strong>, not
          visits — one person counted once however many times they came back — and filtered exactly
          like the sections either side: same launch floor, same bot filter, same exclusion of Ben’s
          own devices. These are the same figures the{' '}
          <strong style={{ color: C.text, fontWeight: 700 }}>Pages &amp; Masterclass</strong> tab shows,
          read from the same endpoint, so the two cannot disagree.{' '}
          <strong style={{ color: C.text, fontWeight: 700 }}>Page loads</strong> is the one visits
          number here, shown greyed for comparison only — it is the same people, counted once per
          visit, and nothing above is a share of it.
        </div>
      </div>
    </div>
  );
};

// ── SECTION 1 — OPT-IN PAGE ─────────────────────────────────────────────────
// A FLAT LIST, the same shape the sales table already uses: one row per source,
// Landed / Opted in / Rate, then a Total. The old nested paid → campaign →
// placement → ad tree is gone. Ad performance is its own section now, and
// mixing "where did the traffic come from" with "what did it cost" in one place
// was the confusion this restructure removes.
//
// DATE BASIS: identical to the sales table BY CONSTRUCTION, not by coincidence.
// Both read from the same `fromISO = floorFrom(null)` declared once in
// /utm-stats (admin.js) and passed to the opt-in query and the sales query
// alike — floored at the 14 Aug launch. There is no second basis to reconcile.
//
// ROW ORDER: the three CAMPAIGNS first, by campaign name, carrying
// WHOLE-CAMPAIGN landed and opted in — deliberately NOT the nominated-ad
// figures the Ad metrics cards show. Then the named organic links in the API's
// own order, then Direct, then one catch-all.
//
// Only the campaign rows expand. Organic rows have no placement data to show.
const OptinTable = ({ data }) => {
  const paid    = data?.paid    || { campaigns: [] };
  const organic = data?.organic || { links: [] };
  // `direct` is the UNTAGGED group: arrivals that carried no UTMs at all. It
  // used to be one row called "Direct" — see the backend's classify() for why
  // that label was a guess. `rows` breaks it down by the referrer the opt-in
  // page has always stored.
  //
  // FALLBACK IS LOAD-BEARING, NOT DEFENSIVE PADDING. Backend and frontend are
  // separate deploys, so this file can be live against a backend that predates
  // `rows`. When it is, `rows` is undefined and the group renders as the single
  // row it always did — no crash, no empty section, no missing traffic.
  const direct  = data?.direct  || { label: 'Untagged', landed: 0, optedIn: 0 };
  const directRows = Array.isArray(direct.rows) ? direct.rows : null;
  const totals  = data?.totals  || { landed: 0, optedIn: 0 };
  const trackedSince = fmtTrackingDate(data?.landedTrackingSince);

  const isOtherPaid    = (r) => /^Other \(/.test(r.label);
  const isOtherOrganic = (r) => /^Other \(/.test(r.label);

  const campaigns    = (paid.campaigns || []).filter(c => !isOtherPaid(c));
  const organicNamed = (organic.links  || []).filter(l => !isOtherOrganic(l));

  // ONE catch-all row, summing the two the API keeps apart: paid traffic on an
  // unmapped campaign, and organic traffic on a link that is not in the specs
  // list. Both mean "we could not name this"; two rows saying that in a flat
  // table says less, not more. SUMMED, never dropped, so the Total still adds
  // up — and if either ever becomes non-zero it still shows.
  const sum = (rows, f) => rows.reduce((n, r) => n + (r[f] || 0), 0);
  const otherRows = [
    ...(paid.campaigns || []).filter(isOtherPaid),
    ...(organic.links  || []).filter(isOtherOrganic),
  ];
  const other = { landed: sum(otherRows, 'landed'), optedIn: sum(otherRows, 'optedIn') };

  return (
    <div>
      <OptinColumnHeader />

      {campaigns.map(c => <CampaignSourceRow key={c.id || c.label} campaign={c} />)}

      {organicNamed.map(l => (
        <OptinRow key={l.label} label={l.label} landed={l.landed} optedIn={l.optedIn} />
      ))}

      {/* UNTAGGED — a group with its own rows, read like a campaign and its
          placements: the parent carries the total, the indented children say
          where the traffic actually came from. Each child is either a named
          source (hostname, or a group name where several hosts are genuinely
          one site), or the honest "we have nothing" row. The children
          sum to the parent; the backend asserts that before this renders. */}
      <OptinRow label={direct.label} landed={direct.landed} optedIn={direct.optedIn} />
      {directRows && directRows.map(r => (
        <OptinRow key={r.label} label={r.label} landed={r.landed} optedIn={r.optedIn} indent />
      ))}
      <OptinRow label="Other / untracked" landed={other.landed} optedIn={other.optedIn} muted />

      {/* The row that proves everything above adds up. Reads the endpoint's own
          totals, which its server-side reconciliation asserts against the same
          buckets these rows are built from — so the table cannot quietly
          disagree with the total sitting under it. */}
      <div style={{ marginTop: 8 }}>
        <OptinRow label="Total" landed={totals.landed} optedIn={totals.optedIn} isTotal />
      </div>

      {trackedSince && (
        <div style={{ paddingTop: 10, fontSize: 10, color: C.accent, lineHeight: 1.6 }}>
          <strong style={{ color: C.text, fontWeight: 700 }}>Earliest landing shown: {trackedSince}</strong> —
          the first arrival inside the timeframe selected above, and the same basis the Sales page
          section uses. On All time that is the day the opt-in page started recording landings, and
          nothing before it was ever written; on a shorter timeframe it is simply where that window
          starts, not where tracking began. The Ad metrics
          section is ALL TIME and covers a different period on purpose: it measures the ads, not the
          funnel. Where opt-ins outnumber landings the two cover different periods, so no rate is
          shown. Tap a campaign to see which placements its traffic came from.
        </div>
      )}
    </div>
  );
};

const OPTIN_COLS = ['Landed', 'Opted in', 'Rate'];

const OptinColumnHeader = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, padding: '0 8px 6px', borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
    <span style={{ flex: 1, minWidth: 0 }} />
    {OPTIN_COLS.map(h => (
      <span key={h} style={{ width: h === 'Rate' ? RATE_W : COL_W, textAlign: 'right', fontSize: 9.5, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {h}
      </span>
    ))}
  </div>
);

// One row of the flat table. Rate is computed through the SHARED rateOf(), so
// these rows inherit its suppression: no rate on zero landings, and none where
// opt-ins outnumber landings because the two cover different periods.
const OptinRow = ({ label, landed, optedIn, isTotal, muted, indent, onClick, open, hasDrill, tint }) => (
  <div
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '7px 8px',
      paddingLeft: indent ? 20 : 8,
      background: isTotal ? C.barTrack : tint ? CAMPAIGN_TINT : 'transparent',
      borderBottom: isTotal ? 'none' : `1px solid ${C.border}`,
      borderRadius: isTotal ? 8 : 0,
      cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none',
    }}
  >
    <span style={{
      flex: 1, minWidth: 0,
      fontSize: isTotal ? 13 : indent ? 11.5 : 12.5,
      fontWeight: isTotal ? 800 : 600,
      color: muted || indent ? C.accent : C.heading,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {label}
      {hasDrill && (
        <span style={{ fontSize: 10, color: C.accent, marginLeft: 6, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      )}
    </span>
    <span style={{ width: COL_W, textAlign: 'right', fontSize: isTotal ? 13 : 12, fontWeight: isTotal ? 800 : 600, color: landed > 0 ? C.heading : C.accent }}>
      {landed}
    </span>
    <span style={{ width: COL_W, textAlign: 'right', fontSize: isTotal ? 13 : 12, fontWeight: isTotal ? 800 : 600, color: optedIn > 0 ? C.heading : C.accent }}>
      {optedIn}
    </span>
    <span style={{ width: RATE_W, textAlign: 'right', fontSize: 11, fontWeight: 600, color: C.accent }}>
      {rateOf(landed, optedIn) || '—'}
    </span>
  </div>
);

// A campaign row that expands into its placement breakdown — the Source block
// that used to live inside the ad metric cards, moved here and widened from one
// ad to the whole campaign.
//
// The breakdown is the endpoint's own `slots` list, which its reconciliation
// ALREADY asserts sums to the campaign row above it, so these rows cannot drift
// from that number. Traffic whose tag carried no recognisable placement sits in
// an 'Unspecified' slot and is COUNTED, never dropped — which is exactly why
// the sum holds.
const CampaignSourceRow = ({ campaign }) => {
  const [open, setOpen] = useState(false);
  const slots = campaign.slots || [];
  return (
    <div>
      <OptinRow
        label={campaign.label}
        landed={campaign.landed}
        optedIn={campaign.optedIn}
        onClick={slots.length ? () => setOpen(o => !o) : undefined}
        open={open}
        hasDrill={slots.length > 0}
        tint
      />
      {open && slots.map(s => (
        <OptinRow
          key={s.label}
          label={`↳ ${s.label}`}
          landed={s.landed}
          optedIn={s.optedIn}
          indent
          muted={/unspecified/i.test(s.label)}
        />
      ))}
      {open && slots.length > 0 && (
        <div style={{ fontSize: 9.5, color: C.accent, lineHeight: 1.5, padding: '2px 8px 6px 20px' }}>
          Where this campaign’s traffic was shown, from the ad URL tag on each landing.
          Anything whose tag carried no placement is counted under “Unspecified” rather
          than dropped, so these rows sum to the campaign above.
        </div>
      )}
    </div>
  );
};


// If the backend's own reconciliation assertions failed, say so at the top of
// the panel. A number that does not add up has to be visible, not hidden.
const ReconciliationWarning = ({ reconciliation }) => {
  if (!reconciliation || reconciliation.ok) return null;
  return (
    <div style={{ background: '#FDECEC', border: '1px solid #E9A3A3', borderRadius: 10, padding: '14px 18px', marginBottom: 20, fontSize: 12.5, color: '#8A2B2B', lineHeight: 1.6 }}>
      <strong>These numbers do not add up.</strong> The server found{' '}
      {reconciliation.issues.length} reconciliation {reconciliation.issues.length === 1 ? 'failure' : 'failures'} —
      treat the figures below as unreliable until this is fixed.
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {reconciliation.issues.map((i, n) => (
          <li key={n} style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {i.check}: expected {i.expected}, got {i.actual} ({i.delta > 0 ? '+' : ''}{i.delta})
          </li>
        ))}
      </ul>
    </div>
  );
};

// Click-to-copy box — the whole box is the click target. Copies its url to the
// clipboard and briefly shows "Copied!" (own state, so one box copying doesn't
// flash the others). Reverts after ~1.5s.
// `hint` is optional and additive — the three original link lists don't pass it
// and render exactly as before. The masterclass list does, because Ben reads
// those while writing a DM and needs to know which one to reach for.
// `multiline` is additive too: the DM-reply script below is plain text with a
// deliberate blank line in it, and the default white-space rule would fold
// that into one run. The clipboard gets the exact string either way — this
// only changes what the box SHOWS. Every URL box leaves it unset and renders
// exactly as before.
// `reference` is additive as well: the DM-reply cards copy their SHORT link
// (go.benmilston.com/watch…) and show the full tracked address it forwards to
// small underneath, for reference only — the click still copies `url`.
const CopyBox = ({ label, url, hint, multiline = false, reference }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {});
    } catch { /* clipboard unavailable — no-op */ }
  };

  return (
    <div
      onClick={handleCopy}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopy(); } }}
      style={{
        background: copied ? C.greenBg : C.card,
        border: `1px solid ${copied ? C.green : C.border}`,
        borderRadius: 10,
        padding: '12px 14px',
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => { if (!copied) e.currentTarget.style.background = C.barTrack; }}
      onMouseLeave={(e) => { if (!copied) e.currentTarget.style.background = C.card; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 5 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.heading }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: copied ? C.green : C.accent, whiteSpace: 'nowrap' }}>
          {copied ? '✓ Copied!' : 'Click to copy'}
        </span>
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: C.accent, lineHeight: 1.5, marginBottom: 6 }}>
          {hint}
        </div>
      )}
      <div style={{ fontSize: 11, color: C.text, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5, ...(multiline ? { whiteSpace: 'pre-wrap' } : {}) }}>
        {url}
      </div>
      {reference && (
        <div style={{ fontSize: 9.5, color: C.accent, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5, marginTop: 4 }}>
          {reference}
        </div>
      )}
    </div>
  );
};

// ── STARTING 5 — DM REPLY SCRIPT ────────────────────────────────────────────
// The message Ben sends when someone comments on the Starting 5 ad asking for
// the masterclass — sent BEFORE the "Starting 5 — DM reply" link, so it sits
// directly above that box. Plain text, not a URL: nothing counts it, nothing
// on the backend knows about it, and it is deliberately the only copy box on
// this tab whose contents are not built from config/adNames.js. Copied
// verbatim — every character, the line breaks and the blank line are part of
// what Ben pastes (the ":)", the four dots and the lower-case "free
// masterclass" included). Do not tidy it.
const STARTER_5_DM_REPLY_LABEL = 'Starter 5 — DM Reply';
const STARTER_5_DM_REPLY_TEXT =
  'Heya Ben :) hope you\'re well!\n' +
  'You commented saying you wanted the link to my free masterclass (the one about helping you understand why you\'re still stuck in old conditioning/patterns even if you\'re aware of it....and what to do to break free)\n' +
  '\n' +
  'Would you like me to send you the link here or would you prefer via email?';
// Which DM link the script goes above. Matched on the URL's campaign slug
// rather than the label, so renaming "Starting 5" in adNames.js does not
// silently drop the script off the page.
const isStarter5Link = (link) => /[?&]utm_campaign=starter-5(&|$)/.test(link.url || '');

// A sub-heading INSIDE a link section, so one section can hold two kinds of
// link without becoming two sections. Used by the paid campaign block, which now
// carries both the URL that goes in the ad and the one Ben pastes into a DM —
// same three campaigns, same paid attribution, different way of reaching the
// person. Splitting them into two collapsibles would suggest they are two
// different sets of campaigns, which is exactly what they are not.
const LinkGroupHeading = ({ children, note }) => (
  <div style={{ paddingTop: 2 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.heading }}>{children}</div>
    {note && <div style={{ fontSize: 10.5, color: C.accent, lineHeight: 1.5, marginTop: 2 }}>{note}</div>}
  </div>
);

// Collapsible section — header toggles open/closed.
//
// GENERALISED rather than duplicated: it still renders a `links` list of
// copy-boxes exactly as before (the three link blocks below pass that), and it
// now also wraps arbitrary `children`, which is what the two traffic sections
// use. `meta` replaces the hardcoded "N links" caption, and `defaultOpen` lets
// the traffic sections start expanded while the link blocks stay collapsed.
//
// `note` covers the case the organic link lists introduced: those links now
// arrive from /utm-stats, so there is a moment before they exist and a case
// where they never do. When `note` is set the body renders that sentence
// INSTEAD of the boxes, and the header drops its "N links" count — an empty
// list with a confident "0 links" beside it would read as "Ben has no links",
// which is the one thing it must never say.
const CollapsibleSection = ({ title, links, meta, note, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const caption = meta != null ? meta : (note ? null : (links ? `${links.length} links` : null));

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {caption && <span style={{ fontSize: 11, color: C.accent }}>{caption}</span>}
          <span style={{ fontSize: 13, color: C.accent, transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </span>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: links ? 10 : 0 }}>
          {note
            ? <div style={{ fontSize: 12, color: C.accent, lineHeight: 1.6 }}>{note}</div>
            : children != null
              ? children
              : links.map(link => <CopyBox key={link.url} label={link.label} url={link.url} hint={link.hint} />)}
        </div>
      )}
    </div>
  );
};

export const AdminTraffic = () => {
  // Opens on the saved "Opt-in Copy Change" marker when it exists; fetches wait for tfReady.
  const { range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, from, to, ready: tfReady } =
    useTimeframe('all', { preferMarkerName: DEFAULT_MARKER_NAME });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tracked separately from `data`, because the catch below deliberately
  // substitutes a zero-filled PLACEHOLDER so the tiles still render. That
  // placeholder is indistinguishable from a real all-zero response, so without
  // this flag a failed fetch would silently show an empty links list.
  const [failed, setFailed] = useState(false);

  // The masterclass row reads /wistia-stats rather than /utm-stats. That is the
  // SAME endpoint the Pages & Masterclass tab uses, so this row and that tab
  // cannot disagree about how many people landed — they are literally the same
  // number, not two computations that happen to match today.
  const [mc, setMc] = useState(null);


  useEffect(() => {
    if (!tfReady) return;
    setLoading(true);
    Promise.all([
      getAdminUtmStats(from, to).then(r => r.data).catch(() => { setFailed(true); return PLACEHOLDER; }),
      // A wistia failure must not blank the whole tab — the two existing rows
      // do not depend on it, so it fails to null and the masterclass row says so.
      getAdminWistiaStats(from, to).then(r => r.data?.masterclass ?? null).catch(() => null),
    ])
      .then(([utm, mcData]) => { setData(utm); setMc(mcData); })
      .finally(() => setLoading(false));
  }, [from, to, tfReady]);

  // The copy-box links come from the backend now — the same lists that name the
  // rows. Three states have to be told apart, because two of them would
  // otherwise render as a confident empty list:
  //   loading      — not fetched yet
  //   failed       — the request errored
  //   list missing — reached a backend deployed before that list existed
  //                  (separate deploys: the frontend can ship first)
  const utmLinks = data?.utmLinks || EMPTY_UTM_LINKS;
  const fetchNote =
    loading ? 'Loading links…'
    : failed ? 'Couldn’t load the links — the Traffic API didn’t respond. Your links themselves are unaffected; reload the page to try again.'
    : null;

  // Checked PER LIST, not once on utmLinks itself. `campaigns` shipped a deploy
  // after `optin`/`sales`, so a backend can legitimately return utmLinks with
  // two of the three keys — and the missing one must say so rather than render
  // as an empty box with a confident "0 links" beside it.
  //
  // Tests data.utmLinks, NOT the EMPTY_UTM_LINKS fallback — the fallback's keys
  // are arrays by construction, so checking it would report "loaded fine, no
  // links" for a response that never carried the field at all.
  const noteFor = (key) =>
    fetchNote ||
    (Array.isArray(data?.utmLinks?.[key])
      ? null
      : 'Couldn’t load the links — the server responded without them, which usually means the backend is still deploying. Reload in a minute.');

  // Untagged = the Direct tier's opt-ins, straight from the server-computed
  // hierarchy, so the tiles and the table can never disagree.
  // Closed-header captions. Read from the SAME objects the Total rows render,
  // so the collapsed summary and the expanded total are one number, not two.
  const optinTot = data?.totals || { landed: 0, optedIn: 0 };
  // The Sales page section follows this tab's range like every other section
  // (the sales video switch is gone): /utm-stats' own salesPage for [from, to].
  const salesPage = failed ? null : (data?.salesPage ?? null);
  const salesTot = salesPage?.totals || { landed: 0, cta: 0, purchased: 0 };
  const optinMeta = `${optinTot.landed} landed · ${optinTot.optedIn} opted in`;
  const salesMeta = loading ? '…' : failed ? 'couldn’t load'
    : `${salesTot.landed} landed · ${salesTot.cta} CTA · ${salesTot.purchased} bought`;
  // Same rhythm as its two neighbours. Reads from the same object the expanded
  // total row uses, so a closed header and an open section cannot disagree.
  // PEOPLE, like its two neighbours. Was mc.pageLoads — a visits number sitting
  // above two person numbers in the same caption. uniqueVisitorsAll is the SAME
  // field the Pages & Masterclass card's "Unique visitors" tile reads, so the
  // two surfaces cannot disagree.
  const mcMeta = mc
    ? `${mc.uniqueVisitorsAll ?? 0} landed · ${mc.totalPlays ?? 0} watched · ${mc.cta?.clicked?.count ?? 0} clicked through`
    : '—';

  const untrackedCount = data?.direct?.optedIn ?? 0;
  const trackedCount = (data?.total || 0) - untrackedCount;

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      <SectionHeader
        title="Traffic & UTM Tracking"
        sub="Where your opt-ins are coming from — based on UTM parameters in your ad and link URLs"
      />

      <TimeframeControlWithDates
        range={range} setRange={setRange}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
      />

      <ReconciliationWarning reconciliation={data?.reconciliation} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.accent, fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          {/* Summary tiles */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
            {[
              {
                label: 'Total Opt-ins', value: data?.total || 0,
                // Was hardcoded 'All time' — wrong as soon as this tab gained a
                // timeframe control, since the tile's OWN value already reflects
                // whatever range is selected. Read the selected preset's own
                // label instead, so the tile can never claim a period the count
                // above it isn't actually scoped to.
                // rangeLabel also knows the named markers ("Since Opt-in Copy
                // Change"), so a marker's name shows here as the presets' do.
                sub: rangeLabel(range),
                // People who filled in the form again in this range although
                // they had signed up before — shown beside the count, never in
                // it (backend services/returningSignups.js). null = not read.
                extra: data?.returningSignups != null ? returningLine(data.returningSignups) : null,
              },
              { label: 'UTM Tagged', value: trackedCount, sub: 'Have a source attached' },
              // sub-label was 'Direct / unknown source' — "Direct" is a word the
              // row beneath it stopped using when that row learned to read the
              // referrer. The VALUE is untouched: still data.direct.optedIn.
              { label: 'Untagged', value: untrackedCount, sub: 'No UTM tags on the link' },
              {
                label: 'Tag Rate',
                value: data?.total > 0 ? Math.round((trackedCount / data.total) * 100) + '%' : '—',
                sub: 'Of opt-ins have UTMs',
                accent: trackedCount > 0 ? C.green : null,
              },
            ].map(tile => (
              <div key={tile.label} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 22px', flex: 1, minWidth: 130 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{tile.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: tile.accent || C.heading, lineHeight: 1 }}>{tile.value}</div>
                {tile.sub && <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>{tile.sub}</div>}
                {tile.extra && <div style={{ fontSize: 11, color: C.heading, fontWeight: 600, marginTop: 4 }}>{tile.extra}</div>}
              </div>
            ))}
          </div>

          {/* THREE SECTIONS, ALL CLOSED BY DEFAULT, each with its own totals
              on its header — otherwise the page loads showing nothing but three
              titles. Each caption reads from the SAME object that section's own
              total row uses, so a closed header and an expanded total cannot
              disagree.

              They are peers because they answer three different questions:
              where opt-in traffic came from, what the sales page did with it,
              and what the ads cost. Opening "Opt-in page" used to show the
              first and the third at once, which is the confusion this split
              removes. There is deliberately no combined total across them. */}
          <CollapsibleSection title="Opt-in page" meta={optinMeta}>
            <OptinTable data={data} />
          </CollapsibleSection>

          <CollapsibleSection title="Masterclass" meta={mcMeta}>
            <MasterclassTable mc={mc} />
          </CollapsibleSection>

          <CollapsibleSection title="Sales page" meta={salesMeta}>
            <SalesTable sales={salesPage} />
          </CollapsibleSection>

          {/* The ad-spend section that used to sit here lives on the Ad
              Metrics tab now (AdSpend.jsx). This tab is about where traffic
              came from; that one is about what it cost. */}
        </>
      )}

      {/* ── UTM Links — Share & Copy ──
          Still rendered outside the `loading` branch above, so the section and
          its headers are always on the page. All three blocks now come from the
          API — the same lists that name the rows — so each carries a `note` for
          the loading, failed and list-missing cases rather than rendering as an
          empty list. Order on the tab is unchanged: paid, opt-in, sales. */}
      <div style={{ marginTop: 32 }}>
        <SectionHeader
          title="UTM Links — Share & Copy"
          sub="Pre-built tagged links for sharing. Click any box to copy the full URL to your clipboard."
        />
        {/* ONE SECTION, TWO GROUPS. Both lists are the SAME three campaigns in
            the same order, built from the same slug array in adNames.js. The DM
            versions are not a different campaign set and must not read as one —
            they are the same paid traffic arriving by a different route, and
            they roll into the same campaign row and the same cost per opt-in.
            The DM group renders only when the backend actually sent it, so a
            frontend deployed ahead of its backend shows the ad URLs exactly as
            it does today rather than an empty heading. */}
        <CollapsibleSection
          title="Meta ad campaign URLs (paid)"
          meta={noteFor('campaigns') ? null : `${utmLinks.campaigns.length + utmLinks.campaignsDm.length + utmLinks.campaignsDm.filter(isStarter5Link).length} links`}
          note={noteFor('campaigns')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <LinkGroupHeading note="Goes in the ad. Meta fills in the placement and which ad they clicked.">
              In the ad
            </LinkGroupHeading>
            {utmLinks.campaigns.map(link => (
              <CopyBox key={link.url} label={link.label} url={link.url} hint={link.hint} />
            ))}
            {utmLinks.campaignsDm.length > 0 && (
              <>
                <LinkGroupHeading note="For replying by DM when someone comments on an ad. Still counts as paid traffic on that campaign — Meta just can't fill in the placement or ad when you paste it by hand.">
                  Replying to a comment by DM
                </LinkGroupHeading>
                {utmLinks.campaignsDm.map(link => (
                  <Fragment key={link.url}>
                    {/* The reply script rides directly above its link. Same
                        CopyBox, same click-to-copy, same "Copied!" — only the
                        contents are text rather than a URL. */}
                    {isStarter5Link(link) && (
                      <CopyBox label={STARTER_5_DM_REPLY_LABEL} url={STARTER_5_DM_REPLY_TEXT} multiline />
                    )}
                    {/* The short link is what gets pasted; it forwards to
                        link.url with every tag intact. A backend that has not
                        sent shortUrl yet leaves the card exactly as before. */}
                    <CopyBox
                      label={link.label}
                      url={link.shortUrl || link.url}
                      hint={link.hint}
                      reference={link.shortUrl ? link.url : undefined}
                    />
                  </Fragment>
                ))}
              </>
            )}
          </div>
        </CollapsibleSection>
        <CollapsibleSection title="Free Masterclass UTM links" links={utmLinks.optin} note={noteFor('optin')} />
        <CollapsibleSection title="RY Sales Page Links" links={utmLinks.sales} note={noteFor('sales')} />
        {/* Last, because it is the newest and the most situational — the other
            three are links Ben publishes, these are links he sends one to one.
            Same component, same CopyBox, same 375px behaviour (the URL already
            wraps on wordBreak:'break-all'); the only addition is the per-link
            hint, which the other lists don't pass. */}
        <CollapsibleSection
          title="Masterclass — direct link (when you send it yourself)"
          links={utmLinks.masterclassDirect}
          note={noteFor('masterclassDirect')}
        />
        {/* A DIFFERENT DESTINATION from everything above: these point at
            booking-hub's Clarity Call page, not at Ben's funnel pages, so they
            are their own section rather than a group inside one. Same
            component, same CopyBox, same per-link hint as the masterclass
            list. Nothing on this tab counts them — the person's source shows
            on their contact card in booking-hub. */}
        <CollapsibleSection
          title="Clarity Call booking links"
          links={utmLinks.clarityCall}
          note={noteFor('clarityCall')}
        />
      </div>
    </div>
  );
};
